import { useEffect, useRef, useState } from "react";

/** Height of the fixed public header; the indicator never enters this band. */
export const HEADER_OFFSET = 64;
const MIN_THUMB = 32;

/**
 * Touch screens: phones (iPhone especially) draw the page scroll indicator
 * over the fixed header and ignore track margins. The native page bar is
 * hidden on touch screens in index.css and this red thumb is drawn instead,
 * confined to the area below the header. Mouse devices keep the native bar.
 */
export default function PageScrollIndicator({ topOffset = HEADER_OFFSET }: { topOffset?: number }) {
  const [enabled, setEnabled] = useState(false);
  const thumbRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const update = () => setEnabled(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let frame = 0;
    let hideTimer: number | undefined;

    const measure = () => {
      frame = 0;
      const el = thumbRef.current;
      if (!el) return;
      const doc = document.documentElement;
      const viewH = window.innerHeight;
      const docH = doc.scrollHeight;
      const track = viewH - topOffset;
      const maxScroll = docH - viewH;
      if (maxScroll <= 1 || track <= MIN_THUMB) {
        el.style.opacity = "0";
        return;
      }
      const thumbH = Math.max(MIN_THUMB, Math.round((track * viewH) / docH));
      const progress = Math.min(1, Math.max(0, window.scrollY / maxScroll));
      el.style.height = `${thumbH}px`;
      el.style.transform = `translate3d(0, ${Math.round((track - thumbH) * progress)}px, 0)`;
    };

    const onScroll = () => {
      const el = thumbRef.current;
      if (!frame) frame = requestAnimationFrame(measure);
      if (el) el.style.opacity = "1";
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => {
        if (thumbRef.current) thumbRef.current.style.opacity = "0";
      }, 900);
    };
    const onResize = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (frame) cancelAnimationFrame(frame);
      window.clearTimeout(hideTimer);
    };
  }, [enabled, topOffset]);

  if (!enabled) return null;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed right-0.5 z-40"
      style={{ top: topOffset, bottom: 0, width: 4 }}
    >
      <div
        ref={thumbRef}
        data-page-scroll-thumb=""
        className="w-full rounded-full bg-primary transition-opacity duration-300 will-change-transform"
        style={{ opacity: 0, height: MIN_THUMB }}
      />
    </div>
  );
}
